'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Columns, Check } from 'lucide-react';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean; // Some columns like checkbox and title might be required
}

export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'checkbox', label: 'Select', visible: true, required: true },
  { key: 'title', label: 'Title', visible: true, required: true },
  { key: 'source', label: 'Source', visible: true },
  { key: 'brand', label: 'Brand', visible: true },
  { key: 'category', label: 'Category', visible: true },
  { key: 'language', label: 'Language', visible: false },
  { key: 'visibility', label: 'Visibility', visible: true },
  { key: 'reach', label: 'Reach', visible: true },
  { key: 'visitors', label: 'Visitors', visible: true },
  { key: 'media_type', label: 'Media Type', visible: true },
  { key: 'date', label: 'Date', visible: true },
  { key: 'sentiment', label: 'Sentiment', visible: true },
];

interface ColumnVisibilityFilterProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
}

export const ColumnVisibilityFilter = ({
  columns,
  onColumnsChange
}: ColumnVisibilityFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColumns, setTempColumns] = useState(columns);

  // Update tempColumns when columns prop changes
  useEffect(() => {
    setTempColumns(columns);
  }, [columns]);

  const toggleColumn = (key: string) => {
    const updatedColumns = tempColumns.map(col => 
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    setTempColumns(updatedColumns);
  };

  const handleApply = () => {
    onColumnsChange(tempColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempColumns(columns); // Reset to original state
    setIsOpen(false);
  };

  const visibleCount = columns.filter(col => col.visible).length;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-gray-600 border-gray-300 hover:bg-gray-100"
        >
          <Columns className="h-4 w-4" />
          Columns ({visibleCount})
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 bg-white">
        <SheetHeader className="border-b pb-4">
          <SheetTitle>Column Visibility</SheetTitle>
          <div className="text-sm text-gray-600">
            Choose which columns to display in the table and export.
          </div>
        </SheetHeader>
        <div className="mt-6 space-y-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {tempColumns.map((column) => (
              <div
                key={column.key}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  column.visible ? 'bg-[#8A6FE8]/5 border-[#8A6FE8]/20' : 'bg-gray-50 border-gray-200'
                } ${column.required ? 'opacity-60' : ''}`}
              >
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => !column.required && toggleColumn(column.key)}
                      disabled={column.required}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      column.visible
                        ? 'bg-[#8A6FE8] border-[#8A6FE8] text-white'
                        : 'bg-white border-gray-300'
                    } ${column.required ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      {column.visible && <Check className="w-3 h-3" />}
                    </div>
                  </div>
                  <span className={`text-sm ${column.required ? 'text-gray-500' : 'text-gray-900'}`}>
                    {column.label}
                    {column.required && <span className="text-xs text-gray-400 ml-1">(required)</span>}
                  </span>
                </label>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-gray-200">
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const allVisible = tempColumns.map(col => ({ ...col, visible: true }));
                  setTempColumns(allVisible);
                }}
                className="flex-1"
              >
                Show All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const essentialOnly = tempColumns.map(col => ({
                    ...col,
                    visible: col.required || ['title', 'source', 'date', 'sentiment'].includes(col.key)
                  }));
                  setTempColumns(essentialOnly);
                }}
                className="flex-1"
              >
                Essential Only
              </Button>
            </div>
          </div>
        </div>
        <div className="border-t bg-white p-4 flex gap-2">
          <Button
            onClick={handleCancel}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-[#8A6FE8] hover:bg-[#7A5FD8] text-white"
          >
            Apply
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}; 